if (!customElements.get('cart-update-form')) {
class CartUpdateForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.cart-form');
    this.updateButton = this.querySelector('[data-cart-update]');
    this.checkoutButton = this.querySelector('[data-cart-checkout]');
    this.status = this.querySelector('[data-cart-status]');
    this.updateTimer = null;
    this.abortController = null;
    this.isUpdating = false;
    this.isDirty = false;

    if (!this.form || !this.updateButton) return;

    this.updateButton.disabled = true;
    this.addEventListener('input', this.handleInput.bind(this));
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  disconnectedCallback() {
    window.clearTimeout(this.updateTimer);
    if (this.abortController) this.abortController.abort();
  }

  handleInput(event) {
    if (!event.target.matches('[data-cart-quantity]') || this.isUpdating) return;

    this.isDirty = true;
    this.updateButton.disabled = false;
    this.announce('', false);
    window.clearTimeout(this.updateTimer);

    if (!event.target.value || !event.target.checkValidity()) return;
    this.scheduleUpdate();
  }

  handleSubmit(event) {
    const quantitySubmittedWithEnter = !event.submitter && document.activeElement?.matches('[data-cart-quantity]');
    const updateRequested = event.submitter === this.updateButton || quantitySubmittedWithEnter;

    if (!updateRequested) {
      if (this.isUpdating) event.preventDefault();
      return;
    }

    event.preventDefault();
    window.clearTimeout(this.updateTimer);

    if (this.hasInvalidQuantity()) {
      this.form.reportValidity();
      return;
    }

    this.updateCart();
  }

  scheduleUpdate(delay = 900) {
    window.clearTimeout(this.updateTimer);
    this.updateTimer = window.setTimeout(() => this.updateCart(), delay);
  }

  async updateCart() {
    if (!this.form || !this.isDirty || this.isUpdating || this.hasInvalidQuantity()) return;

    const submittedChanges = this.collectChangedLines().sort((a, b) => b.position - a.position);
    if (!submittedChanges.length) {
      this.isDirty = false;
      this.updateButton.disabled = true;
      return;
    }

    const [change, ...queuedChanges] = submittedChanges;
    const focusSnapshot = this.captureFocus();
    const quantityInputs = this.quantityInputs();

    this.isUpdating = true;
    this.isDirty = false;
    this.setInputsDisabled(quantityInputs, true);
    this.updateButton.disabled = true;
    if (this.checkoutButton) this.checkoutButton.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.announce(this.dataset.updatingMessage, false);

    this.abortController = new AbortController();
    let mutationCommitted = false;

    try {
      const response = await fetch(this.dataset.changeUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: change.lineKey,
          quantity: change.quantity,
          sections: [this.dataset.sectionId],
          sections_url: window.location.pathname,
        }),
        signal: this.abortController.signal,
      });
      mutationCommitted = response.ok;
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || this.dataset.updateError);

      const sectionHtml = cart.sections?.[this.dataset.sectionId];
      if (!sectionHtml) throw new Error(this.dataset.updateError);

      const responseDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const replacement = responseDocument.querySelector('cart-update-form');
      if (!replacement) throw new Error(this.dataset.updateError);
      customElements.upgrade(replacement);

      this.abortController = null;
      this.replaceWith(replacement);
      replacement.refreshCartCount(cart.item_count);

      window.requestAnimationFrame(() => {
        replacement.announce(replacement.dataset.updatedMessage, false);
        replacement.applyQueuedChanges(queuedChanges);
        replacement.restoreFocus(focusSnapshot, change);
      });
    } catch (error) {
      if (error.name === 'AbortError' && !mutationCommitted) return;

      if (mutationCommitted) {
        this.abortController = null;
        window.location.reload();
        return;
      }

      this.abortController = null;
      this.isUpdating = false;
      this.removeAttribute('aria-busy');
      const failedInput = this.findQuantityInput(change.position);
      if (failedInput) failedInput.value = failedInput.dataset.initialQuantity;
      this.setInputsDisabled(quantityInputs, false);
      this.isDirty = this.collectChangedLines().length > 0;
      this.updateButton.disabled = !this.isDirty;
      if (this.checkoutButton) this.checkoutButton.disabled = false;
      this.announce(error.message || this.dataset.updateError, true);
      if (failedInput) failedInput.focus({ preventScroll: true });
    }
  }

  quantityInputs() {
    return Array.from(this.querySelectorAll('[data-cart-quantity]'));
  }

  collectChangedLines() {
    return this.quantityInputs()
      .map((input) => ({
        lineKey: input.dataset.lineKey,
        position: Number(input.dataset.linePosition),
        quantity: Number(input.value),
        initialQuantity: Number(input.dataset.initialQuantity),
      }))
      .filter((line) => line.quantity !== line.initialQuantity);
  }

  hasInvalidQuantity() {
    return this.quantityInputs().some((input) => !input.value || !input.checkValidity());
  }

  findQuantityInput(position) {
    return this.quantityInputs().find((input) => Number(input.dataset.linePosition) === position) || null;
  }

  setInputsDisabled(inputs, disabled) {
    inputs.forEach((input) => { input.disabled = disabled; });
  }

  captureFocus() {
    const activeInput = document.activeElement?.matches('[data-cart-quantity]') ? document.activeElement : null;
    if (!activeInput) return null;
    return { position: Number(activeInput.dataset.linePosition) };
  }

  applyQueuedChanges(queuedChanges) {
    let appliedUpdate = false;

    queuedChanges.forEach((change) => {
      const input = this.findQuantityInput(change.position);
      if (!input) return;
      input.value = String(change.quantity);
      appliedUpdate = true;
    });

    if (!appliedUpdate) return;
    this.isDirty = true;
    this.updateButton.disabled = false;
    if (!this.hasInvalidQuantity()) this.scheduleUpdate(0);
  }

  restoreFocus(focusSnapshot, submittedChange) {
    if (!focusSnapshot) return;

    const inputs = this.quantityInputs();
    if (!inputs.length) {
      const emptyState = this.querySelector('[data-cart-empty]');
      if (emptyState) emptyState.focus({ preventScroll: true });
      return;
    }

    let focusTarget;
    const focusedLineWasRemoved = submittedChange.quantity === 0
      && focusSnapshot.position === submittedChange.position;

    if (focusedLineWasRemoved) {
      const nextIndex = Math.min(focusSnapshot.position - 1, inputs.length - 1);
      focusTarget = inputs[nextIndex];
    } else {
      focusTarget = this.findQuantityInput(focusSnapshot.position);
    }

    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  announce(message, isError) {
    if (!this.status) return;
    this.status.setAttribute('role', isError ? 'alert' : 'status');
    this.status.textContent = message;
  }

  refreshCartCount(itemCount) {
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = itemCount;
    });
    document.querySelectorAll('[data-cart-link]').forEach((link) => {
      const itemLabel = itemCount === 1 ? link.dataset.cartItem : link.dataset.cartItems;
      link.setAttribute('aria-label', `${link.dataset.cartLabel}, ${itemCount} ${itemLabel}`);
    });
  }
}

customElements.define('cart-update-form', CartUpdateForm);
}
